import { AlertCircle, Gauge, Pause, Play, Volume2, VolumeX } from 'lucide-react'
import * as React from 'react'
import { Button } from '@/components/ui/button'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import {
	formatTime,
	PLAYBACK_SPEEDS,
	PROGRESS_HANDLE_SIZE_PX,
	useAudioPlayer,
	VOLUME_STEP,
} from '@/hooks/artifact-viewer/transcript'
import { useTranslation } from '@/hooks/useTranslation'
import { cn } from '@/lib/utils'

interface AudioPlayerProps {
	src: string
	audioRef: React.RefObject<HTMLAudioElement | null>
	onTimeUpdate?: (timeMs: number) => void
	className?: string
}

export function AudioPlayer({ src, audioRef, onTimeUpdate, className }: AudioPlayerProps) {
	const { t } = useTranslation()
	const [isHoveringProgress, setIsHoveringProgress] = React.useState(false)

	const player = useAudioPlayer({ src, audioRef, onTimeUpdate })
	const {
		isPlaying,
		currentTime,
		duration,
		playbackSpeed,
		volume,
		isMuted,
		audioError,
		progress,
		progressRef,
		togglePlay,
		handleProgressClick,
		handleProgressKeyDown,
		handleSpeedChange,
		toggleMute,
		handleVolumeChange,
		clearErrorAndRetry,
	} = player

	if (audioError) {
		return (
			<div className={cn('flex items-center gap-2 text-destructive', className)}>
				{/* biome-ignore lint/a11y/useMediaCaption: transcript is displayed in panel below */}
				<audio ref={audioRef} src={src} preload="metadata" />
				<AlertCircle className="h-4 w-4 shrink-0" />
				<span className="text-sm">{t('pages.projectDetail.transcript.audio.error')}</span>
				<Button
					type="button"
					variant="ghost"
					size="sm"
					onClick={clearErrorAndRetry}
					className="h-7 px-2 text-xs"
				>
					{t('pages.projectDetail.transcript.audio.retry')}
				</Button>
			</div>
		)
	}

	return (
		<TooltipProvider>
			<div className={cn('flex items-center gap-2', className)}>
				{/* biome-ignore lint/a11y/useMediaCaption: transcript is displayed in panel below */}
				<audio ref={audioRef} src={src} preload="metadata" />

				<Tooltip>
					<TooltipTrigger asChild>
						<Button
							type="button"
							variant="ghost"
							size="icon"
							onClick={togglePlay}
							aria-label={
								isPlaying
									? t('pages.projectDetail.transcript.audio.pause')
									: t('pages.projectDetail.transcript.audio.play')
							}
							className="h-8 w-8 rounded-full hover:bg-[#8c52ff]/10 hover:text-[#8c52ff]"
						>
							{isPlaying ? (
								<Pause className="h-4 w-4" fill="currentColor" />
							) : (
								<Play className="h-4 w-4" fill="currentColor" />
							)}
						</Button>
					</TooltipTrigger>
					<TooltipContent>
						{isPlaying
							? t('pages.projectDetail.transcript.audio.pause')
							: t('pages.projectDetail.transcript.audio.play')}
					</TooltipContent>
				</Tooltip>

				<span className="text-xs text-muted-foreground font-mono min-w-9">
					{formatTime(currentTime)}
				</span>

				<div
					ref={progressRef}
					role="slider"
					tabIndex={0}
					aria-label={t('pages.projectDetail.transcript.audio.progress')}
					aria-valuenow={Math.round(currentTime)}
					aria-valuemin={0}
					aria-valuemax={Math.round(duration)}
					aria-valuetext={`${formatTime(currentTime)} / ${formatTime(duration)}`}
					className="relative flex-1 h-1.5 bg-muted rounded-full cursor-pointer group"
					onClick={handleProgressClick}
					onKeyDown={handleProgressKeyDown}
					onMouseEnter={() => setIsHoveringProgress(true)}
					onMouseLeave={() => setIsHoveringProgress(false)}
				>
					<div
						className="absolute left-0 top-0 h-full bg-[#8c52ff] rounded-full transition-all"
						style={{ width: `${progress}%` }}
					/>
					<div
						className={cn(
							'absolute top-1/2 -translate-y-1/2 w-3 h-3 bg-[#8c52ff] rounded-full shadow-md transition-opacity',
							isHoveringProgress ? 'opacity-100' : 'opacity-0'
						)}
						style={{ left: `calc(${progress}% - ${PROGRESS_HANDLE_SIZE_PX / 2}px)` }}
					/>
				</div>

				<span className="text-xs text-muted-foreground font-mono min-w-9">{formatTime(duration)}</span>

				<div className="flex items-center gap-1 group">
					<Tooltip>
						<TooltipTrigger asChild>
							<Button
								type="button"
								variant="ghost"
								size="icon"
								onClick={toggleMute}
								aria-label={
									isMuted
										? t('pages.projectDetail.transcript.audio.unmute')
										: t('pages.projectDetail.transcript.audio.mute')
								}
								className="h-7 w-7 rounded-full hover:bg-[#8c52ff]/10 hover:text-[#8c52ff]"
							>
								{isMuted || volume === 0 ? (
									<VolumeX className="h-4 w-4" />
								) : (
									<Volume2 className="h-4 w-4" />
								)}
							</Button>
						</TooltipTrigger>
						<TooltipContent>
							{isMuted
								? t('pages.projectDetail.transcript.audio.unmute')
								: t('pages.projectDetail.transcript.audio.mute')}
						</TooltipContent>
					</Tooltip>
					<input
						type="range"
						min="0"
						max="1"
						step={VOLUME_STEP}
						value={isMuted ? 0 : volume}
						onChange={handleVolumeChange}
						className="w-14 h-1 bg-muted rounded-full appearance-none cursor-pointer opacity-0 group-hover:opacity-100 transition-opacity [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-2.5 [&::-webkit-slider-thumb]:h-2.5 [&::-webkit-slider-thumb]:bg-[#8c52ff] [&::-webkit-slider-thumb]:rounded-full [&::-moz-range-thumb]:border-0 [&::-moz-range-thumb]:w-2.5 [&::-moz-range-thumb]:h-2.5 [&::-moz-range-thumb]:bg-[#8c52ff] [&::-moz-range-thumb]:rounded-full"
						aria-label={t('pages.projectDetail.transcript.audio.volume')}
					/>
				</div>

				<DropdownMenu>
					<Tooltip>
						<TooltipTrigger asChild>
							<DropdownMenuTrigger asChild>
								<Button
									type="button"
									variant="ghost"
									size="sm"
									className="h-7 px-1.5 rounded-full hover:bg-[#8c52ff]/10 hover:text-[#8c52ff] text-xs font-medium"
								>
									<Gauge className="h-4 w-4 mr-1" />
									{playbackSpeed}x
								</Button>
							</DropdownMenuTrigger>
						</TooltipTrigger>
						<TooltipContent>{t('pages.projectDetail.transcript.audio.speed')}</TooltipContent>
					</Tooltip>
					<DropdownMenuContent align="end">
						{PLAYBACK_SPEEDS.map((speed) => (
							<DropdownMenuItem
								key={speed}
								onClick={() => handleSpeedChange(speed)}
								className={cn(
									'cursor-pointer',
									speed === playbackSpeed && 'bg-[#8c52ff]/10 text-[#8c52ff]'
								)}
							>
								{speed}x
							</DropdownMenuItem>
						))}
					</DropdownMenuContent>
				</DropdownMenu>
			</div>
		</TooltipProvider>
	)
}
